import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import pluralize from "pluralize";
import {
  Flex,
  Button,
  Modal,
  Box,
  Text,
  Dropdown,
  CriteriaTag,
  IconSvg,
  List,
  IconSvgButton
} from "samba-ui";
import styled from "styled-components";
import { loadAllAdminDashboards } from "../api";
import SetFilters from "./SetFilters";
import ShowPeriscopeUrl from "./ShowPeriscopeUrl";
import RegistrationUsersOverview from "./RegistrationUsersOverview";
import getPeriscopeUrlParams from "../utils/getPeriscopeUrlParams";

const StyledIconSvg = styled(IconSvg)`
  cursor: pointer;
  float: right;
`;

const AddNewDashboardModal = ({
  isOpen,
  onClose,
  onSubmit,
  registrations = [],
  userDashboards = {}
}) => {
  const [dashboards, setDashboards] = useState({});
  useEffect(() => {
    const req = async () => {
      const res = await loadAllAdminDashboards();
      const { data } = await res.json();
      setDashboards(data);
    };
    req();
  }, []);

  const [selectedDashboard, setSelectedDasboard] = useState(null);
  const [selectedFilters, setSelectedFilters] = useState(null);
  const [showPeriscopeUrl, setShowPeriscopeUrl] = useState(null);
  const [
    selectedDashboardsWithFilter,
    setSelectedDashboardsWithFilter
  ] = useState([]);

  const onAddAnotherDashboardClick = () => {
    const dashboard = selectedDashboard;
    dashboard.filters = selectedFilters;
    const data = [...selectedDashboardsWithFilter, dashboard];
    setSelectedDashboardsWithFilter(data);
    setSelectedDasboard(null);
    setSelectedFilters(null);
    setShowPeriscopeUrl(false);
  };

  const onDeleteSelectedDashboards = index => {
    const copy = selectedDashboardsWithFilter.slice();
    copy.splice(index, 1); // remove the index
    setSelectedDashboardsWithFilter(copy);
  };

  const findInSelectedDashboards = id => {
    return selectedDashboardsWithFilter.find(({ value }) => value === id);
  };

  const dropDownOptions = Object.keys(dashboards).map(id => {
    return {
      label: dashboards[id].name,
      value: id,
      isDisabled: !!userDashboards[id] || findInSelectedDashboards(id)
    };
  });

  const numberOfDisabledDashboard = dropDownOptions.reduce(
    (acc, { isDisabled }) => (isDisabled ? acc + 1 : acc),
    0
  );

  const availableDashboardCount =
    Object.keys(dashboards).length - numberOfDisabledDashboard;

  const onRequestClose = () => {
    setSelectedDasboard(null);
    setSelectedFilters(null);
    setShowPeriscopeUrl(false);
    setSelectedDashboardsWithFilter([]);
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onRequestClose={onRequestClose} width={800}>
      <Modal.Header borderColor="#d6d6d6 !important">
        <Text>Approve User's Registration</Text>
      </Modal.Header>
      <Modal.Content minHeight={350}>
        {registrations.length ? (
          <Box>
            <Box mb={2}>
              <Text fontSize={1} fontWeight={500}>
                {`Approving registrations for the below ${pluralize(
                  "user",
                  registrations.length
                )}`}
              </Text>
            </Box>
            <RegistrationUsersOverview registrations={registrations} />
          </Box>
        ) : null}

        {selectedDashboardsWithFilter.length ? (
          <List.Header borderColor="#d6d6d6 !important">
            <Flex justifyContent="space-between">
              <Text fontSize={1} color="secondary">
                New Dashboards
              </Text>
            </Flex>
          </List.Header>
        ) : null}

        {selectedDashboardsWithFilter.map((dashboard, index) => (
          <List.Item key={index} borderColor="#d6d6d6 !important">
            <Flex alignItems="center" justifyContent="space-between">
              <Box width={1 / 6}>
                <Text>{dashboard.label}</Text>
              </Box>
              <Flex width={4 / 6}>
                {dashboard.filters.map(filter => (
                  <Box mr={2} key={filter.label}>
                    <CriteriaTag>{filter.label}</CriteriaTag>
                  </Box>
                ))}
              </Flex>
              <Box width={1 / 6}>
                <StyledIconSvg
                  name="close"
                  color="red"
                  onClick={() => onDeleteSelectedDashboards(index)}
                />
              </Box>
            </Flex>
          </List.Item>
        ))}

        {availableDashboardCount ? (
          <Box my={3}>
            <Text fontSize={1} fontWeight={500}>
              Assign a dashboard
            </Text>
            <Dropdown
              options={dropDownOptions}
              selected={selectedDashboard}
              onSelect={selected => {
                // find the defaults and set there for selected filters
                const defaultFilters = dashboards[selected.value].filters
                  .filter(f => f.isDefault)
                  .map(f => ({ label: f.name, value: f.id, removeIcon: true }));

                setSelectedDasboard(selected);
                setSelectedFilters(defaultFilters);
                setShowPeriscopeUrl(false);
              }}
            />
            {selectedDashboard && (
              <Box mt={2}>
                <SetFilters
                  filters={dashboards[selectedDashboard.value].filters || []}
                  selectedFilters={selectedFilters}
                  setSelectedFilters={filters => {
                    setShowPeriscopeUrl(false);
                    setSelectedFilters(filters);
                  }}
                />
              </Box>
            )}

            {selectedFilters && (
              <Flex mt={3} alignItems="center" justifyContent="flex-end">
                <Box mr={3}>
                  {showPeriscopeUrl ? (
                    <ShowPeriscopeUrl
                      {...getPeriscopeUrlParams(
                        dashboards[selectedDashboard.value],
                        selectedFilters
                      )}
                    />
                  ) : (
                    <IconSvgButton
                      name="check-o"
                      variant="destructiveBordered"
                      onClick={() => setShowPeriscopeUrl(true)}
                    >
                      Test before adding
                    </IconSvgButton>
                  )}
                </Box>
                <IconSvgButton
                  name="add-o"
                  variant="primaryBordered"
                  onClick={onAddAnotherDashboardClick}
                >
                  Add another dashboard
                </IconSvgButton>
              </Flex>
            )}
          </Box>
        ) : null}
      </Modal.Content>
      <Modal.Actions justifyContent="flex-end" borderColor="#d6d6d6 !important">
        <Flex>
          <Box mr={3}>
            <Button variant="secondary" onClick={onRequestClose}>
              Cancel
            </Button>
          </Box>
          <Button
            variant="primary"
            onClick={() => onSubmit(selectedDashboardsWithFilter)}
          >
            {registrations.length ? "Approve" : "Assign"}
          </Button>
        </Flex>
      </Modal.Actions>
    </Modal>
  );
};

AddNewDashboardModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  dropDownOptions: PropTypes.array,
  userDashboards: PropTypes.object,
  registrations: PropTypes.array
};

export default AddNewDashboardModal;
